/**
 * Soft-wrap resolution for the plaintext/code editor. Pure and framework-free
 * so it can be unit-tested in isolation and reused by the store and the App.
 */

/** Language keys treated as prose for default wrapping. Everything else is code. */
export const SOFT_WRAP_PROSE_KEYS: ReadonlySet<string> = new Set(['markdown', 'plaintext']);

/** The persisted wrap configuration the resolver reads. */
export interface SoftWrapConfig {
  softWrapDefault: boolean;
  softWrapExcludeCode: boolean;
  softWrapByLanguage: Record<string, boolean>;
}

/**
 * Resolve whether a document soft-wraps, most-specific layer first:
 *   1. per-document override (transient) — when defined, wins outright;
 *   2. per-language exception — when the language key has an entry;
 *   3. "don't wrap code" — forces non-prose languages off when enabled;
 *   4. the global default.
 */
export function resolveSoftWrap(
  langKey: string,
  perDoc: boolean | undefined,
  cfg: SoftWrapConfig,
): boolean {
  if (perDoc !== undefined) return perDoc;
  const exception = cfg.softWrapByLanguage[langKey];
  if (exception !== undefined) return exception;
  if (cfg.softWrapExcludeCode && !SOFT_WRAP_PROSE_KEYS.has(langKey)) return false;
  return cfg.softWrapDefault;
}

/**
 * Parse a persisted `softWrapByLanguage` JSON blob into a clean
 * `Record<string, boolean>`, dropping any non-boolean values and returning `{}`
 * for null / empty / malformed input.
 */
export function parseSoftWrapByLanguage(raw: string | null | undefined): Record<string, boolean> {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'boolean') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}
